import { Suspense } from 'react'
import { Navigate } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { Dashboard } from '@factorialco/f0-react/dist/experimental'
import { AvailableModules } from 'generated/featureDefinitions'
import {
  PerformanceAnalyticsEmployeeTalentPageDocument,
  PerformanceAnalyticsEmployeeTalentWidgetPoliciesDocument,
} from 'generated/resources/graphql'
import { compact } from 'lodash'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { useStrictParams } from 'modules/core/hooks/useStrictParams'
import { isModuleAvailabilityEnabled } from 'modules/core/lib/isModuleAvailabilityEnabled'
import { WidgetsProvider } from 'modules/core/lib/widgets'

import { buildGoalsWidget } from 'modules/profile/components/Insights/Goals'
import { buildPerformanceWidget } from 'modules/profile/components/Insights/Performance'
import { buildTrainingsWidget } from 'modules/profile/components/Insights/Trainings'
import { WidgetDashboard } from 'modules/profile/components/Insights/widgets'

const TalentPage = () => {
  const { company } = useCurrentSession()
  const { id } = useStrictParams<'id'>()
  const { navigation } = useNavigation()

  const { loading, data } = useQuery(PerformanceAnalyticsEmployeeTalentPageDocument, {
    variables: {
      companyId: company.id,
      employeeId: Number(id),
      moduleIds: [
        AvailableModules.trainings,
        AvailableModules.review_process,
        AvailableModules.goals,
      ],
    },
    skip: !id,
  })

  const employee = data?.employees.employee
  const canAccessTalentPage = employee?._policies?.seeEmployeeTalentPage.can ?? false

  if (loading || !employee) {
    return (
      <Box padding="s32">
        <Dashboard.Skeleton />
      </Box>
    )
  }

  if (!canAccessTalentPage) {
    return <Navigate replace to={navigation.employees.id.fullPath({ id })} />
  }

  const moduleAvailabilities = data?.companies.moduleAvailabilitiesConnection.nodes

  const trainingsEnabled = isModuleAvailabilityEnabled(
    moduleAvailabilities,
    AvailableModules.trainings
  )
  const performanceEnabled = isModuleAvailabilityEnabled(
    moduleAvailabilities,
    AvailableModules.review_process
  )

  const goalsEnabled = isModuleAvailabilityEnabled(moduleAvailabilities, AvailableModules.goals)

  const widgets = compact([
    performanceEnabled && buildPerformanceWidget(),
    trainingsEnabled && buildTrainingsWidget(),
    goalsEnabled && buildGoalsWidget(),
  ])

  return (
    <Box padding="s32">
      <Suspense fallback={<Dashboard.Skeleton />}>
        <WidgetsProvider widgets={widgets} onWidgetsEndLoading={() => {}}>
          <WidgetDashboard
            widgets={widgets}
            employeeId={employee.id}
            policiesQuery={PerformanceAnalyticsEmployeeTalentWidgetPoliciesDocument}
            extractPolicies={(policyData) =>
              policyData?.employees.employee?._policiesCollection ?? []
            }
            args={{ employeeId: employee.id, accessId: employee.accessId }}
          />
        </WidgetsProvider>
      </Suspense>
    </Box>
  )
}

export default TalentPage
